import React from 'react';
import { Lock } from 'lucide-react';
import DataTable from '../../components/nucleus/DataTable';
import { schemaOf, titleOf, tintOf, isSealed, ownerOf } from '../nucleus/GraphModel';
import { NodeMark } from '../../components/nucleus/faces';
import { writerOf } from '../../nucleus/access';
import { usePlace } from '../../nucleus/place';
import { ago, whenOf } from '../../nucleus/format';
import { readPref } from './graphData';

// THE ROWS — the graph as a table (DataTable), an inbox of nodes: the kind's
// square with the schema's icon, the title, the kind, who wrote it, when it last
// changed and when it was made. Pressing a row opens it beside them; ↑/↓ walk
// the rows while one is open (the page's). The server sorts: a head's press
// asks the address for its sort. When the rows narrow (a node open beside
// them), columns give way in `keep` order: the kind and when it changed hold
// longest.

// the columns chosen before the table kept its own layout: what they started as
const LEGACY = 'nucleus:graph-table-columns';
const DEFAULT_SHOWN = ['schema', 'app', 'when', 'created'];

// the address's sorts, as a column and a direction
const SORT_OF = {
    title: { id: 'title', dir: 'asc' }, 'title-desc': { id: 'title', dir: 'desc' },
    updated: { id: 'when', dir: 'desc' }, 'updated-asc': { id: 'when', dir: 'asc' },
    created: { id: 'created', dir: 'desc' }, 'created-asc': { id: 'created', dir: 'asc' },
};
const sortId = ({ id, dir }) => Object.keys(SORT_OF).find((k) => SORT_OF[k].id === id && SORT_OF[k].dir === dir) || 'updated';

// who wrote it: the app, else the person ("You" for the viewer, quieter); in a
// group never "you" for someone else
function Writer({ node, group, userId }) {
    const { group: space } = usePlace();
    const w = writerOf(node, userId, !!group, space);
    if (!w) return <span className="gx-dim">—</span>;
    return w === 'You' ? <span className="gx-dim">You</span> : w;
}

const At = ({ iso }) => <time dateTime={iso} title={whenOf(iso, { year: true }) || undefined}>{ago(iso, { dateAfter: 7 })}</time>;

export default function NodesTable({
    nodes, total, sort, onSort, openId, onOpen, actionsOf, empty, loading, wrap, group = null, userId = null, owner = true,
}) {
    const chosen = readPref(LEGACY, DEFAULT_SHOWN);
    const off = (id) => !chosen.includes(id);
    const columns = [
        { id: 'title', label: 'Title', lock: true, min: 220, grow: 3, className: 'gx-cell-title', value: titleOf,
          cell: (n) => {
              const schema = schemaOf(n);
              const sealed = Object.values(n.value_json || {}).some(isSealed);
              return <>
                  <NodeMark schema={schema} schemaId={n.schema_id} hue={tintOf(schema)} size={22} />
                  <span className="gx-title">{titleOf(n)}</span>
                  {sealed && <Lock size={10} strokeWidth={2.2} className="gx-lock" aria-label="encrypted" />}
              </>;
          } },
        { id: 'schema', label: 'Schema', min: 110, grow: 1.2, keep: 1, off: off('schema'), cell: (n) => schemaOf(n) },
        { id: 'app', label: 'Written by', min: 110, grow: 1.2, keep: 3, off: off('app'), cell: (n) => <Writer node={n} group={group} userId={userId} /> },
        // in a group everything is the group's: no Owner column
        owner && { id: 'owner', label: 'Owner', min: 110, grow: 1.2, keep: 5, off: off('owner'), cell: (n) => ownerOf(n) || <span className="gx-dim">you</span> },
        { id: 'stratum', label: 'Stratum', min: 110, grow: 1.2, keep: 6, off: off('stratum'), cell: (n) => n.stratum },
        { id: 'when', label: 'Changed', min: 92, grow: 0, keep: 2, asc: false, off: off('when'), className: 'gx-cell-when', value: (n) => n.updated_at, cell: (n) => <At iso={n.updated_at} /> },
        { id: 'created', label: 'Made', min: 92, grow: 0, keep: 4, asc: false, off: off('created'), className: 'gx-cell-when', value: (n) => n.created_at, cell: (n) => <At iso={n.created_at} /> },
    ].filter(Boolean);

    return (
        <DataTable
            id="graph-nodes" label="Nodes" columns={columns} rows={nodes} keyOf={(n) => n.id} total={total}
            sort={SORT_OF[sort] || SORT_OF.updated} onSort={(s) => onSort(sortId(s))}
            currentKey={openId} onOpen={(id) => onOpen(id)} actionsOf={actionsOf}
            fit sticky wrap={wrap} empty={empty} loading={loading}
        />
    );
}
